import { Fragment, useRef } from 'react';

import type { ToggleItem, ToggleItemValue } from '@/types';

import { Separator } from '@/components/ui/separator';
import useBreakpoint from '@/hooks/use-break-point';
import useDrawerBottomOffset from '@/hooks/use-drawer-bottom-offset';
import { cn } from '@/lib/utils';

type Props = {
  items: ToggleItem[];
  activeValue?: string;
  onValueChange: (value: ToggleItemValue) => void;
  isDrawerOpen?: boolean;
  className?: string;
  isToastVisible?: boolean;
  drawerHeight?: number;
};

const FloatingButton = ({
  items,
  activeValue,
  onValueChange,
  isDrawerOpen = false,
  className,
  isToastVisible = false,
  drawerHeight,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const isMobile = useBreakpoint('mobile');

  useDrawerBottomOffset(containerRef, { drawerHeight, isDrawerOpen, isToastVisible, isMobile });

  return (
    <div
      ref={containerRef}
      role="group"
      aria-label="View selection toggle"
      className={cn(
        'fixed h-10 bottom-15.5 right-2.5 z-50 p-0.5 bg-background border rounded-full shadow-sm flex items-center gap-px transition-all duration-300',
        {
          'bottom-(--drawer-bottom-offset)': isMobile && isDrawerOpen && !isToastVisible,
          'bottom-[calc(var(--drawer-bottom-offset)+0.5rem)]': isToastVisible,
        },
        className
      )}
    >
      {items.map((item, index) => {
        const Icon = item.icon;
        const isActive = activeValue === item.value;
        const isFirst = index === 0;
        const isLast = index === items.length - 1;

        return (
          <Fragment key={item.value}>
            <button
              onClick={() => onValueChange(item.value)}
              aria-label={item.label}
              aria-pressed={isActive}
              className={cn(
                'h-full flex items-center justify-center px-2.5 py-1.5 cursor-pointer transition-all duration-150 active:scale-95',
                {
                  'rounded-l-full': isFirst,
                  'rounded-r-full': isLast,
                  'bg-active-floating-action': isActive,
                  'hover:bg-gray-100 dark:hover:bg-gray-800': !isActive,
                }
              )}
            >
              <Icon
                className={cn(
                  'size-6 transition-colors',
                  isActive ? 'text-primary' : 'text-foreground'
                )}
              />
            </button>
            {!isLast && <Separator orientation="vertical" />}
          </Fragment>
        );
      })}
    </div>
  );
};

export default FloatingButton;
